import moment from 'moment';
import React, { CSSProperties, useEffect, useState } from 'react'
import { Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import DotLoader from 'react-spinners/DotLoader';
import { getProductionDashboardData } from '../../services/prosumer.service';
import ProsumerProductionChart from '../charts/prosumerProductionChart';

const override: CSSProperties = {
  display: "block",
  margin: "auto",
  marginTop: "250px",
  marginBottom: "200px",
  borderColor: "red",
};

interface ProsumerData {
  localtime: string,
  solar: number,
  net: number,
  import: number
}

const CURRENCY_CONVERSION: number = 5.036800;

const ProsumerProduction = () => {
  let [loading, setLoading] = useState(false);
  const auth = JSON.parse(localStorage.getItem('auth')!);
  const [isChecked, setIsChecked] = useState(false);
  const [selectedDate, setSelectedDate] = useState('dst');
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  });
  const [productionDayData, setProductionDayData] = useState<ProsumerData[]>([]);
  const [productionWeekData, setProductionWeekData] = useState<ProsumerData[]>([]);
  const [productionYearData, setProductionYearData] = useState<ProsumerData[]>([]);

  const [netDayData, setNetDayData] = useState<any>();
  const [solarDayData, setSolarDayData] = useState<any>();
  const [importDayData, setImportDayData] = useState<any>();

  const [netWeekData, setNetWeekData] = useState<any>();
  const [solarWeekData, setSolarWeekData] = useState<any>();
  const [importWeekData, setImportWeekData] = useState<any>();

  const [netYearData, setNetYearData] = useState<any>();
  const [solarYearData, setSolarYearData] = useState<any>();
  const [importYearData, setImportYearData] = useState<any>();

  const [hasSelectGenerated, setHasSelectGenerated] = useState<boolean>(false);
  const [hasSelectConsumed, setHasSelectConsumed] = useState<boolean>(false);
  const [hasSelectNett, setHasSelectNett] = useState<boolean>(true);

  // currency related values
  const [netCurrencyDayData, setNetCurrencyDayData] = useState<any>();
  const [solarCurrencyDayData, setSolarCurrencyDayData] = useState<any>();
  const [importCurrencyDayData, setImportCurrencyDayData] = useState<any>();
  const [netCurrencyWeekData, setNetCurrencyWeekData] = useState<any>();
  const [solarCurrencyWeekData, setSolarCurrencyWeekData] = useState<any>();
  const [importCurrencyWeekData, setImportCurrencyWeekData] = useState<any>();
  const [netCurrencyYearData, setNetCurrencyYearData] = useState<any>();
  const [solarCurrencyYearData, setSolarCurrencyYearData] = useState<any>();
  const [importCurrencyYearData, setImportCurrencyYearData] = useState<any>();

  const color = '#006837';
  let period24Data = productionDayData;
  let periodWeekData = productionWeekData;
  let periodYearData = productionYearData;

  let importData = importDayData;
  let netData = netDayData;
  let solarData = solarDayData;

  if (!isChecked && selectedDate === 'dst') {
    importData = importDayData;
    netData = netDayData;
    solarData = solarDayData;
  } else if (isChecked && selectedDate === 'dst') {
    importData = importCurrencyDayData;
    netData = netCurrencyDayData;
    solarData = solarCurrencyDayData;
  }

  if (!isChecked && selectedDate === 'dsd') {
    importData = importWeekData;
    netData = netWeekData;
    solarData = solarWeekData;
  } else if (isChecked && selectedDate === 'dsd') {
    importData = importCurrencyWeekData;
    netData = netCurrencyWeekData;
    solarData = solarCurrencyWeekData;
  }

  if (!isChecked && selectedDate === 'dsm') {
    importData = importYearData;
    netData = netYearData;
    solarData = solarYearData;
  } else if (isChecked && selectedDate === 'dsm') {
    importData = importCurrencyYearData;
    netData = netCurrencyYearData;
    solarData = solarCurrencyYearData;
  }

  const daylabels = period24Data.map((data) => moment(data.localtime).format('H:mm'));
  const weeklabels = periodWeekData.map((data) => moment(data.localtime).format('DD MMM YY'));
  const yearlabels = periodYearData.map((data) => data.localtime);

  let labels = daylabels;
  if (selectedDate === 'dst') {
    labels = daylabels;
  }
  else if (selectedDate === 'dsd') {
    labels = weeklabels;
  }
  else if (selectedDate === 'dsm') {
    labels = yearlabels;
  }

  const filterDataByTime = (data: ProsumerData[]) => {
    return data.reduce((acc, item) => {
      const date = moment(item.localtime, "YYYY-MM-DD HH:mm:ss");
      const time = date.format("YYYY-MM-DD HH:mm:ss");
      if (!acc[time]) {
        acc[time] = {
          localtime: time,
          import: 0,
          net: 0,
          solar: 0,
        };
      }
      acc[time].import += Number(item.import);
      acc[time].net += Number(item.net);
      acc[time].solar += Number(item.solar);
      return acc;
    }, {} as { [key: string]: ProsumerData });
  }

  const filterDataByDay = (data: ProsumerData[]) => {
    return data.reduce((acc, item) => {
      const date = moment(item.localtime, "YYYY-MM-DD HH:mm:ss");
      const day = date.format("YYYY-MM-DD");
      if (!acc[day]) {
        acc[day] = {
          localtime: day,
          import: 0,
          net: 0,
          solar: 0,
        };
      }
      acc[day].import += Number(item.import);
      acc[day].net += Number(item.net);
      acc[day].solar += Number(item.solar);
      return acc;
    }, {} as { [key: string]: ProsumerData });
  }

  const filterDataByMonth = (data: ProsumerData[]) => {
    return data.reduce((acc, item) => {
      const date = moment(item.localtime, "YYYY-MM-DD HH:mm:ss");
      const month = date.format("MMM");
      if (!acc[month]) {
        acc[month] = {
          localtime: month,
          import: 0,
          net: 0,
          solar: 0,
        };
      }
      acc[month].import += Number(item.import);
      acc[month].net += Number(item.net);
      acc[month].solar += Number(item.solar);
      return acc;
    }, {} as { [key: string]: ProsumerData });
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const dayData = await getProductionDashboardData(
        auth?.userId, moment(new Date(startDate)).format('YYYY-MM-DD'),
        'd',
        auth?.mappedPremises?.map((item: any) => item.premise.PremiseID));
      const weekData = await getProductionDashboardData(
        auth?.userId, moment(new Date(startDate)).format('YYYY-MM-DD'),
        'w',
        auth?.mappedPremises?.map((item: any) => item.premise.PremiseID));
      const yearData = await getProductionDashboardData(
        auth?.userId, moment(new Date(startDate)).format('YYYY-MM-DD'),
        'y',
        auth?.mappedPremises?.map((item: any) => item.premise.PremiseID));

      const resultByTime = filterDataByTime(dayData.data);
      setProductionDayData(Object.values(resultByTime));
      const resultByDate = filterDataByDay(weekData.data);
      setProductionWeekData(Object.values(resultByDate));
      const resultByMonth = filterDataByMonth(yearData.data);
      setProductionYearData(Object.values(resultByMonth));
      setNetDayData(Object.values(resultByTime).map((item: ProsumerData) => item.net));
      setSolarDayData(Object.values(resultByTime).map((item: ProsumerData) => item.solar));
      setImportDayData(Object.values(resultByTime).map((item: ProsumerData) => item.import));
      setNetCurrencyDayData(Object.values(resultByTime).map((item: ProsumerData) => item.net * CURRENCY_CONVERSION));
      setSolarCurrencyDayData(Object.values(resultByTime).map((item: ProsumerData) => item.solar * CURRENCY_CONVERSION));
      setImportCurrencyDayData(Object.values(resultByTime).map((item: ProsumerData) => item.import * CURRENCY_CONVERSION));

      setNetWeekData(Object.values(resultByDate).map((item: ProsumerData) => item.net));
      setSolarWeekData(Object.values(resultByDate).map((item: ProsumerData) => item.solar));
      setImportWeekData(Object.values(resultByDate).map((item: ProsumerData) => item.import));
      setNetCurrencyWeekData(Object.values(resultByDate).map((item: ProsumerData) => item.net * CURRENCY_CONVERSION));
      setSolarCurrencyWeekData(Object.values(resultByDate).map((item: ProsumerData) => item.solar * CURRENCY_CONVERSION));
      setImportCurrencyWeekData(Object.values(resultByDate).map((item: ProsumerData) => item.import * CURRENCY_CONVERSION));

      setNetYearData(Object.values(resultByMonth).map((item: ProsumerData) => item.net));
      setSolarYearData(Object.values(resultByMonth).map((item: ProsumerData) => item.solar));
      setImportYearData(Object.values(resultByMonth).map((item: ProsumerData) => item.import));
      setNetCurrencyYearData(Object.values(resultByMonth).map((item: ProsumerData) => item.net * CURRENCY_CONVERSION));
      setSolarCurrencyYearData(Object.values(resultByMonth).map((item: ProsumerData) => item.solar * CURRENCY_CONVERSION));
      setImportCurrencyYearData(Object.values(resultByMonth).map((item: ProsumerData) => item.import * CURRENCY_CONVERSION));

      setLoading(false);
    }
    fetchData();
  }, [startDate]);

  const handleButtonGroup = (e: any) => {
    if (e.target.name === 'generated') {
      setHasSelectGenerated(true);
      setHasSelectConsumed(false);
      setHasSelectNett(false);
    }
    if (e.target.name === 'consumed') {
      setHasSelectGenerated(false);
      setHasSelectConsumed(true);
      setHasSelectNett(false);
    }
    if (e.target.name === 'nett') {
      setHasSelectGenerated(false);
      setHasSelectConsumed(false);
      setHasSelectNett(true);
    }
  }

  return (
    <div className='dashboardWidget'>
      <div className='widgetTitle'>
        <Row>
          <Col md="4" sm="12" className='mb-3 mb-sm-0'>
            <h3>Prosumer Production</h3>

            <label className='reactdatepicker-label' htmlFor="reactDatePicker">
              <DatePicker
                dateFormat={selectedDate === 'dst' ? "eeee, dd MMM yy" : selectedDate === 'dsd' ? "MMM ′yy" : "yyyy"}
                selected={startDate}
                className="datepicker"
                id='reactDatePicker'
                showMonthYearPicker={selectedDate === 'dsd' ? true : false}
                showYearPicker={selectedDate === 'dsm' ? true : false}
                onChange={date => date && setStartDate(date)}
              />
            </label>
          </Col>
          <Col md='4'>
            <ButtonGroup aria-label="Basic example">
              <Button name='generated' variant={hasSelectGenerated ? 'primary' : 'gray'} onClick={(e) => handleButtonGroup(e)}>Generated</Button>
              <Button name='consumed' variant={hasSelectConsumed ? 'primary' : 'gray'} onClick={(e) => handleButtonGroup(e)}>Import</Button>
              <Button name='nett' variant={hasSelectNett ? 'primary' : 'gray'} onClick={(e) => handleButtonGroup(e)} >Nett</Button>
            </ButtonGroup>
          </Col>
          <Col md="4" sm="12">
            <div className='d-flex align-items-center justify-content-end'>
              <div className='dateSelector border-end pe-4'>
                <Form.Check type="radio" value="dst" id="dst" name='dateSelector' onChange={() => setSelectedDate('dst')} checked={selectedDate === 'dst' ? true : false} label="24" className='form-tag tag-primary' />
                <Form.Check type="radio" value="dsd" id="dsd" name='dateSelector' onChange={() => setSelectedDate('dsd')} checked={selectedDate === 'dsd' ? true : false} label="D" className='form-tag tag-primary' />
                <Form.Check type="radio" value="dsm" id="dsm" name='dateSelector' onChange={() => setSelectedDate('dsm')} checked={selectedDate === 'dsm' ? true : false} label="M" className='form-tag tag-primary' />
              </div>
              <div className='ps-4 switch d-flex align-items-center justify-content-end'>
                <span className='d-block me-2'>kWh</span> <Form.Check type="switch" id="sbds" label="" onClick={() => setIsChecked(!isChecked)} /> <span className='d-block ms-2'>฿</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className='widgetBody'>
        {loading ? <DotLoader
          color={color}
          loading={loading}
          cssOverride={override}
          size={50}
          aria-label="Loading Spinner"
          data-testid="loader"
        /> :
          <ProsumerProductionChart
            data={hasSelectNett ? netData : hasSelectGenerated ? solarData : importData}
            chartLable={hasSelectNett ? 'Nett Data' : hasSelectGenerated ? 'Production Data' : 'Import Data'}
            isChecked={isChecked}
            labels={labels}
            hasSelectGenerated={hasSelectGenerated}
            hasSelectNett={hasSelectNett} />
        } </div>
    </div>
  )
}

export default ProsumerProduction
